import { useLayoutEffect, useRef, useState } from "react";

type UseHasOverflownOptions = {
  direction?: "x" | "y";
  value: string | undefined;
  /** When true, check direct children for overflow instead of the ref element (e.g. when ref is on a wrapper and children have truncate) */
  checkChildren?: boolean;
};

function useHasOverflown<T extends HTMLElement>({
  checkChildren = false,
  direction = "x",
  value,
}: UseHasOverflownOptions) {
  const ref = useRef<T>(null!);
  const [hasOverflown, setHasOverflown] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!value || el == null) return;

    function checkOverflow() {
      if (el == null) return;

      if (checkChildren) {
        const anyChildOverflowing = Array.from(el.children).some(
          (child) =>
            child instanceof HTMLElement &&
            (direction === "x"
              ? child.scrollWidth > child.clientWidth
              : child.scrollHeight > child.clientHeight),
        );
        setHasOverflown(anyChildOverflowing);
      } else if (direction === "x") {
        setHasOverflown(el.scrollWidth > el.clientWidth);
      } else {
        setHasOverflown(el.scrollHeight > el.clientHeight);
      }
    }

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    if (!checkChildren) {
      window.addEventListener("resize", checkOverflow);
    }

    return () => {
      setHasOverflown(false);
      resizeObserver.disconnect();
      if (!checkChildren) {
        window.removeEventListener("resize", checkOverflow);
      }
    };
  }, [value, direction, checkChildren]);

  return { hasOverflown, ref } as const;
}

export default useHasOverflown;
